import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { faChevronRight, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { CanMapV2Service } from 'src/app/core/services/can-map-v2.service';
import { SignalGroups } from 'src/app/pages/UControl/models/ucontrol-config.model';
import {
  CanMapDocument,
  GeneratedUcontrolBlocks,
  SignalDiffResult,
} from 'src/app/pages/UControl/config/models/can-map-block.model';
import { CanMapGeneratorService } from 'src/app/pages/UControl/config/services/can-map-generator.service';
import { CanmapEditablePanelComponent } from './canmap-editable-panel/canmap-editable-panel.component';
import { CanmapSaveConfirmationDialogComponent } from './canmap-save-confirmation-dialog/canmap-save-confirmation-dialog.component';

export interface UcontrolCanmapSaveModalData {
  signalGroups: SignalGroups;
  /** uidCanMapType associato al modello corrente (model.canMapUid), null se il modello non ha una CAN Map */
  canMapUid: string | null;
  modelCode?: string;
}

export interface UcontrolCanmapSaveModalResult {
  saveCanMap: boolean;
  /** Documento CAN Map completo, già mergiato e pronto per il PATCH — presente solo se saveCanMap===true */
  editedCanMapDoc?: CanMapDocument;
}

/**
 * Dialog mostrato al click su "Salva" nella configurazione UControl.
 * Genera i blocchi ucmdm/ucrtc dalla configurazione corrente, confronta con eventuali
 * blocchi già presenti nella CAN Map del modello, e lascia scegliere all'utente se
 * salvare la sola configurazione, la CAN Map, o entrambe.
 *
 * Non esegue mai il PATCH della CAN Map: si limita a restituire il documento mergiato,
 * lasciando al chiamante l'orchestrazione delle chiamate HTTP.
 */
@Component({
  selector: 'app-ucontrol-canmap-save-modal',
  templateUrl: './ucontrol-canmap-save-modal.component.html',
  styleUrls: ['./ucontrol-canmap-save-modal.component.scss'],
})
export class UcontrolCanmapSaveModalComponent implements OnInit {
  @ViewChild(CanmapEditablePanelComponent) editablePanel?: CanmapEditablePanelComponent;

  readonly faChevronRight = faChevronRight;
  readonly faInfoCircle = faInfoCircle;

  loading = false;
  generated: GeneratedUcontrolBlocks | null = null;

  canMapDocument: CanMapDocument | null = null;
  existingUcmdmTcu: any = null;
  existingUcmdmPlatform: any = null;
  hasExistingBlocks = false;
  /** null quando non c'è nulla da confrontare (nessun blocco esistente): evita rumore visivo al primo salvataggio */
  signalDiff: SignalDiffResult | null = null;

  /**
   * Blocchi combinati (tcu+platform) passati al git-diff, calcolati UNA sola volta (mai in un
   * getter): un getter che ritorna un nuovo oggetto ad ogni ciclo di change detection farebbe
   * ricalcolare il diff LCS in continuazione. `null` su "before" => primo salvataggio, nessun
   * confronto (il git-diff mostra solo il blocco proposto in chiaro).
   */
  combinedExistingBlocks: any = null;
  combinedGeneratedBlocks: any = null;
  /** Nomi dei segnali che verranno rimossi, precalcolati per il warning (non un'espressione inline nel template) */
  removedSignalNames = '';
  /** Nomi dei segnali aggiunti, precalcolati per il badge (stessa coerenza del warning sui rimossi) */
  addedSignalNames = '';

  /** true quando il modello non ha nessuna CAN Map associata (model.canMapUid assente) */
  canMapUnavailable = false;
  /** true quando la GET fallisce o il jsonContent non è parsabile: degradiamo senza bloccare l'utente */
  canMapLoadFailed = false;

  saveCanMapChecked = false;

  /** true quando l'utente ha provato a confermare con valori fuori range su "Salva anche su CAN Map" */
  validationError = false;

  /** Specchietto riepilogo modifiche: aperto di default (sempre visibile quando pertinente) */
  summaryExpanded = true;
  /** Viste JSON dettagliate: chiuse di default quando c'è un confronto, aperte al primo salvataggio */
  jsonPreviewExpanded = false;

  constructor(
    public dialogRef: MatDialogRef<UcontrolCanmapSaveModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UcontrolCanmapSaveModalData,
    private canMapV2Service: CanMapV2Service,
    private canMapGeneratorService: CanMapGeneratorService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.generated = this.canMapGeneratorService.generateBlocksFromSignalGroups(this.data.signalGroups);

    // Spunta di default "Salva anche su CAN Map"
    this.saveCanMapChecked = true;

    if (this.generated) {

      // Riferimento stabile (assegnato una sola volta): il blocco proposto di default, mostrato
      // nel git-diff. Non riflette le modifiche manuali della tabella (per non ricalcolare il
      // diff ad ogni tasto premuto) — la tabella stessa mostra già i valori editabili correnti.
      // Include anche ucrtc (tcu+platform): pur non essendo editabile, viene comunque mergiato
      // nella CAN Map e l'utente deve poterlo vedere nel confronto completo.
      this.combinedGeneratedBlocks = [
        this.generated.ucmdm,
        this.generated.ucmdmPlatform,
        this.generated.ucrtc,
        this.generated.ucrtcPlatform,
      ].filter((b): b is NonNullable<typeof b> => b !== null && b !== undefined);
    }

    if (!this.data.canMapUid) {
      this.canMapUnavailable = true;
      return;
    }

    // Interroghiamo sempre la CAN Map, anche senza segnali tele: se la CAN Map contiene già
    // blocchi ucmdm/ucrtc da un salvataggio precedente, l'utente deve poterli vedere e scegliere
    // di rimuoverli (modalità rimozione, gestita più sotto tramite `removalMode`). Saltare la GET
    // qui lascerebbe quei blocchi orfani per sempre, senza che l'utente ne sia mai informato.
    this.loading = true;
    this.canMapV2Service.getCanMapType(this.data.canMapUid).subscribe({
      next: entity => {
        this.loading = false;
        const parsed = this.canMapV2Service.parseCanMapDocument(entity);
        if (!parsed) {
          this.canMapLoadFailed = true;
          return;
        }
        this.canMapDocument = parsed;
        this.existingUcmdmTcu = this.canMapGeneratorService.findExistingUcmdmTcu(parsed.data);
        this.existingUcmdmPlatform = this.canMapGeneratorService.findExistingUcmdmPlatform(parsed.data);
        this.hasExistingBlocks = !!this.existingUcmdmTcu || !!this.existingUcmdmPlatform;

        // Riferimento stabile per il "before" del git-diff (assegnato una sola volta). Include
        // anche ucrtc (se già presente) per lo stesso motivo del blocco generato: dare
        // all'utente il quadro completo di cosa c'è/ci sarà nella CAN Map.
        this.combinedExistingBlocks = this.hasExistingBlocks
          ? [
              this.existingUcmdmTcu,
              this.existingUcmdmPlatform,
              this.canMapGeneratorService.findExistingUcrtcTcu(parsed.data),
              this.canMapGeneratorService.findExistingUcrtcPlatform(parsed.data),
            ].filter((b): b is NonNullable<typeof b> => b !== null && b !== undefined)
          : null;

        // Calcolato ogni volta che c'è un blocco generato, anche al primo salvataggio (nessun
        // blocco CAN Map preesistente): l'utente deve vedere quali segnali sta per aggiungere,
        // non solo quando esiste già qualcosa con cui confrontare (altrimenti il riepilogo
        // resterebbe nascosto proprio nel caso più comune, il primo salvataggio).
        if (this.generated) {
          this.signalDiff = this.canMapGeneratorService.computeSignalDiff(
            this.existingUcmdmPlatform?.signals ?? null,
            this.existingUcmdmTcu?.tx_period_ms ?? null,
            this.generated.ucmdmPlatform.signals,
            this.generated.ucmdm.tx_period_ms,
          );
          this.removedSignalNames = (this.signalDiff.removedSignals ?? []).map(s => s.name).join(', ');
          this.addedSignalNames = this.generated.ucmdmPlatform.signals
            .filter(s => this.signalDiff?.newPars?.has(s.par))
            .map(s => s.name)
            .join(', ');
        }
        // L'accordion JSON parte sempre chiuso (l'utente lo apre se vuole il dettaglio tecnico)
        this.jsonPreviewExpanded = false;
      },
      error: () => {
        // Fail-open: nessun blocco esistente da mostrare, ma l'utente può comunque proseguire
        this.loading = false;
        this.canMapLoadFailed = true;
      },
    });
  }

  /** true quando non ci sono più segnali tele ma la CAN Map contiene già blocchi UControl da rimuovere */
  get removalMode(): boolean {
    return !this.generated && this.hasExistingBlocks;
  }

  get saveCanMapDisabled(): boolean {
    return this.canMapUnavailable;
  }

  get combinedGeneratedBlocksWithEdits(): any {
    if (!this.generated || !this.editablePanel) {
      return this.combinedGeneratedBlocks;
    }

    const editedTxPeriodMs = this.editablePanel.getEditedTxPeriodMs();
    const editedPlatformBlock = this.editablePanel.getEditedPlatformBlock();

    return [
      { ...this.generated.ucmdm, tx_period_ms: editedTxPeriodMs },
      editedPlatformBlock,
      this.generated.ucrtc,
      this.generated.ucrtcPlatform,
    ].filter((b): b is NonNullable<typeof b> => b !== null && b !== undefined);
  }

  private cachedSignalDiffWithEdits: SignalDiffResult | null = null;
  private lastEditedTxPeriod: number | undefined;
  private lastEditedSignalCount: number = 0;

  get signalDiffWithEdits(): SignalDiffResult | null {
    if (!this.generated || !this.editablePanel || !this.signalDiff) {
      return this.signalDiff;
    }

    const editedTxPeriodMs = this.editablePanel.getEditedTxPeriodMs();
    const editedPlatformBlock = this.editablePanel.getEditedPlatformBlock();
    const signalCount = editedPlatformBlock.signals?.length ?? 0;

    // Cache il risultato per evitare ricalcoli ad ogni change detection
    if (
      this.cachedSignalDiffWithEdits !== null &&
      this.lastEditedTxPeriod === editedTxPeriodMs &&
      this.lastEditedSignalCount === signalCount
    ) {
      return this.cachedSignalDiffWithEdits;
    }

    try {
      this.lastEditedTxPeriod = editedTxPeriodMs;
      this.lastEditedSignalCount = signalCount;
      this.cachedSignalDiffWithEdits = this.canMapGeneratorService.computeSignalDiff(
        this.existingUcmdmPlatform?.signals ?? null,
        this.existingUcmdmTcu?.tx_period_ms ?? null,
        editedPlatformBlock.signals,
        editedTxPeriodMs,
      );
      return this.cachedSignalDiffWithEdits;
    } catch (error) {
      console.error('Error computing signal diff with edits:', error);
      return this.signalDiff;
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  /** Chiamato dal template a ogni interazione dell'utente per non lasciare un errore ormai non più pertinente */
  clearValidationError(): void {
    this.validationError = false;
  }

  confirm(): void {
    // Se l'utente ha segnali tele ma NON vuole salvarli su CAN Map, chiedi conferma tramite dialog
    if (!this.saveCanMapChecked && this.generated) {
      this.dialog
        .open(CanmapSaveConfirmationDialogComponent, {
          data: {
            title: 'Conferma salvataggio',
            message:
              'Hai segnali con telemetria attiva, ma "Salva anche su CAN Map" non è spuntato.\n\n' +
              'Sei sicuro di non volere salvare questi segnali sulla CAN Map della centralina/device?',
          },
        })
        .afterClosed()
        .subscribe(confirmed => {
          if (confirmed) {
            this.performSave();
          }
        });
      return;
    }

    this.performSave();
  }

  private performSave(): void {
    if (this.saveCanMapChecked && this.generated && !this.editablePanel?.isValid()) {
      this.validationError = true;
      return;
    }

    let editedCanMapDoc: CanMapDocument | undefined;

    if (this.saveCanMapChecked && this.generated) {
      const editedGenerated: GeneratedUcontrolBlocks = {
        ...this.generated,
        ucmdm: {
          ...this.generated.ucmdm,
          tx_period_ms: this.editablePanel?.getEditedTxPeriodMs() ?? this.generated.ucmdm.tx_period_ms,
        },
        ucmdmPlatform: this.editablePanel?.getEditedPlatformBlock() ?? this.generated.ucmdmPlatform,
      };
      const baseDocument: CanMapDocument = this.canMapDocument ?? { data: [] };
      editedCanMapDoc = this.canMapGeneratorService.mergeIntoCanMapDocument(baseDocument, editedGenerated);
    } else if (this.saveCanMapChecked && this.removalMode && this.canMapDocument) {
      editedCanMapDoc = this.canMapGeneratorService.removeUcontrolBlocks(this.canMapDocument);
    }

    const result: UcontrolCanmapSaveModalResult = {
      saveCanMap: this.saveCanMapChecked,
      editedCanMapDoc,
    };
    this.dialogRef.close(result);
  }
}
